import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Section, Eyebrow, Heading } from './home-ui';
import { InputComponent, TextAreaComponent, phoneUsValidator } from '../../../Utils/Inputs/input-component';
import Selector from '../../../Utils/Select/select-component';
import { ButtonPrimary } from '../../../Utils/Buttons/button-component';
import { setToast } from '../../../Redux/Toast/ToastActions';
import { submitFreeReview } from '../../../Public/home-data';

const EMPTY = { name: '', phone: '', address: '', issueType: '', note: '' };

export default function FreeReviewForm({ data }) {
    const dispatch = useDispatch();
    const [form, setForm] = useState(EMPTY);
    const [hp, setHp] = useState(''); // honeypot
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    if (!data) return null;
    const { eyebrow, heading, headingLine2, sub, issueTypes = [], submitLabel = 'Send Request',
        disclaimer, phone, email, counties = [], responseTime, hours, callCta } = data;

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = 'Your name is required.';
        if (!form.phone.trim()) e.phone = 'Phone number is required.';
        if (!form.address.trim()) e.address = 'Property address is required.';
        if (!form.issueType) e.issueType = 'Please select an issue type.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (hp) return; // bot
        if (!validate()) return;
        setSubmitting(true);
        try {
            await submitFreeReview(form);
            dispatch(setToast('success', 'Thanks, we’ll review the property and reply with the next step.'));
            setForm(EMPTY);
            setErrors({});
        } catch (err) {
            dispatch(setToast('error', 'Something went wrong. Please call us or try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Section id="contact" bg="alt">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_0.85fr]">
                {/* Form */}
                <div className="rounded-card border border-surface-line bg-white p-7 sm:p-9">
                    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                    <Heading line1={heading} line2={headingLine2} className="mt-4 !text-[28px] sm:!text-[34px]" />
                    {sub && <p className="mt-3 text-ink-muted">{sub}</p>}

                    <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
                        <input type="text" value={hp} onChange={(e) => setHp(e.target.value)}
                            className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                        <Field label="Your Name *" error={errors.name}>
                            <InputComponent value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder="Jane Doe" dir="ltr" />
                        </Field>
                        <Field label="Phone Number *" error={errors.phone}>
                            <InputComponent value={form.phone} onChange={(e) => set('phone')(e.target.value)} placeholder="(305) 336-4489" dir="ltr" {...phoneUsValidator(true)} />
                        </Field>
                        <Field label="Property Address *" error={errors.address}>
                            <InputComponent value={form.address} onChange={(e) => set('address')(e.target.value)} placeholder="123 Main St, Miami, FL" dir="ltr" />
                        </Field>
                        <Field label="Issue Type *" error={errors.issueType}>
                            <Selector options={issueTypes} value={form.issueType} onChange={set('issueType')} placeholder="Select one..." />
                        </Field>
                        <Field label="Short Note" error={null}>
                            <TextAreaComponent value={form.note} onChange={(e) => set('note')(e.target.value)} rows={3} placeholder="Anything we should know?" dir="ltr" />
                        </Field>

                        <div className="flex flex-wrap items-center gap-4 pt-2">
                            <ButtonPrimary type="submit" disabled={submitting} className="!rounded-full !bg-brand hover:!bg-navy">
                                {submitting ? 'Sending…' : `${submitLabel} →`}
                            </ButtonPrimary>
                            {disclaimer && <span className="text-sm text-ink-faint">{disclaimer}</span>}
                        </div>
                    </form>
                </div>

                {/* Aside: contact + call CTA */}
                <div className="space-y-4">
                    <InfoRow title="Contact">
                        {phone && <p><span className="text-ink-faint">Phone : </span><a href="tel:3053364489" className="text-navy hover:text-brand">{phone}</a></p>}
                        {email && <p className="break-all"><span className="text-ink-faint">Email : </span><a href={`mailto:${email}`} className="text-navy hover:text-brand">{email}</a></p>}
                    </InfoRow>
                    {counties.length > 0 && (
                        <InfoRow title="Coverage">
                            {counties.map((c) => <p key={c} className="text-ink">{c}</p>)}
                        </InfoRow>
                    )}
                    <InfoRow title="Response Time">
                        {responseTime && <p className="text-ink">{responseTime}</p>}
                        {hours && <p className="text-ink-faint">{hours}</p>}
                    </InfoRow>
                    {callCta && (
                        <div className="rounded-card bg-navy p-6 text-white">
                            <div className="text-sm text-white/70">{callCta.eyebrow}</div>
                            <div className="mt-1 font-display text-2xl font-bold">{callCta.label}</div>
                            <a href={callCta.href} className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 font-display font-semibold text-white transition hover:bg-white hover:text-navy">
                                {callCta.icon} {callCta.phone}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </Section>
    );
}

function Field({ label, error, children }) {
    return (
        <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>
            {children}
            {error && <span className="mt-1 block text-sm text-red-600">{error}</span>}
        </label>
    );
}

function InfoRow({ title, children }) {
    return (
        <div className="rounded-card border border-surface-line bg-white p-5">
            <div className="mb-2 font-display text-sm font-bold uppercase tracking-wide text-brand">{title}</div>
            <div className="space-y-1 text-sm">{children}</div>
        </div>
    );
}
